/*
 *******************************************************************************
 *
 *  Filename:   /redux/store.js
 *
 *  Syntax:     NA
 *
 *  Synopsis:   Store for Redux. Selected slices are persisted to localStorage
 *              via redux-persist so per-screen filter settings survive refresh.
 *
 *  Author:     Norman J. Nolasco [ Archetact Software ]
 *
 *  Created:    Saturday, January 13, 2024 - 9:36 PM (CST)
 *
 *  Revisions:
 *              2024/01/13  NJN Initial Build
 *              2026/04/20  NJN Add redux-persist for patientcalls + formsites
 *
 *  Copyright (c) 2024 - Archetact Software - All Rights Reserved.
 *  Unauthorized copying of this file via any medium is strictly prohibited.
 *  Proprietary and Confidential.
 *
 *******************************************************************************
 */

import { configureStore, combineReducers } from "@reduxjs/toolkit";
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage"; // localStorage
import { composeWithDevTools } from "@redux-devtools/extension";

import aiMedicalNotesReducer from "./slices/AIMedicalNotes";
import aiMedicalVisitReducer from "./slices/AIMedicalVisit";
import aiSessions from "./slices/AISessions";
import commonReducer from "./slices/common";
import companyReducer from "./slices/companies";
import companylicenses from "./slices/companylicenses";
import companyproducts from "./slices/companyproducts";
import companyprofile from "./slices/companyprofile";
import components from "./slices/components";
import counterReducer from "./slices/counter";
import licenses from "./slices/licenses";
import listReducer from "./slices/lists";
import mobile from "./slices/mobile";
import navigationgroups from "./slices/navigationgroups";
import navigations from "./slices/navigations";
import patients from "./slices/patients";
import procedures from "./slices/procedures";
import productgroups from "./slices/productgroups";
import products from "./slices/products";
import roles from "./slices/roles";
import calls from "./slices/calls";
import callprompts from "./slices/callprompts";
import library from "./slices/library";
import sessions from "./slices/sessions";
import userprofile from "./slices/userprofile";
import usersReducer from "./slices/users";
import imrfs from "./slices/imrfs";
import patientcalls from "./slices/patientcalls";
import formsites from "./slices/formsites";
import screenings from "./slices/screenings";
import dashboard from "./slices/dashboard";
import notifications from "./slices/notifications";

const rootReducer = combineReducers({
  aiMedicalNotes: aiMedicalNotesReducer,
  aiMedicalVisit: aiMedicalVisitReducer,
  aiSessions: aiSessions,
  common: commonReducer,
  companies: companyReducer,
  companylicenses: companylicenses,
  companyproducts: companyproducts,
  companyprofile: companyprofile,
  components: components,
  counter: counterReducer,
  licenses: licenses,
  lists: listReducer,
  mobile: mobile,
  navigationgroups: navigationgroups,
  navigations: navigations,
  patients: patients,
  procedures: procedures,
  productgroups: productgroups,
  products: products,
  roles: roles,
  calls: calls,
  sessions: sessions,
  userprofile: userprofile,
  users: usersReducer,
  imrfs: imrfs,
  patientcalls: patientcalls,
  formsites: formsites,
  callprompts: callprompts,
  library: library,
  screenings: screenings,
  dashboard: dashboard,
  notifications: notifications,
});

// Only persist slices that hold per-screen filter / column preferences.
// Other slices are ephemeral (API cache, UI dialog state) and shouldn't be
// frozen across refreshes.
const persistConfig = {
  key: "onrounds",
  version: 1,
  storage,
  whitelist: ["patientcalls", "formsites", "dashboard"],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore(
  {
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          // redux-persist dispatches actions that carry non-serializable payloads
          // (rehydrate, etc.) — ignore them instead of spamming warnings.
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }),
  },
  composeWithDevTools(),
);

export const persistor = persistStore(store);
